import { ASSISTANT_WIDGET_DESIGN_FONT_FAMILY, mergeRemoteAssistantWidgetConfig, resolveAssistantWidgetConfig } from "./utils/public_assistant_widget_config.mjs"
import { assistantFrameMessageTargetOrigin, trustedAssistantFrameMessage } from "./utils/assistant_parent_message.js"

async function init() {
  const script = document.currentScript || document.querySelector("script[data-assistant-widget]")
  // Before anything reads location.href. The config module copies the current
  // URL into parent_url/landing_url, which travel to analytics, page-view rows
  // and Front Desk transcripts — the continuation must be out of the address
  // bar by then or it lands in all three.
  const verificationContinuation = takeVerificationContinuationFromUrl()
  const config = await loadRemoteAssistantWidgetConfig(resolveAssistantWidgetConfig(script))
  config.deferWidgetFrame = Boolean(config.testMode)
  config.forceDesktopBreakpoint = Boolean(config.setupPreview)
  config.widgetUrl = withAssistantFrameMode(config.widgetUrl, "embedded")

  // The emailed "Log-in" link returns here, to the salon's own
  // page, instead of our standalone widget. Boot the frame with the one-time
  // continuation so the widget reopens where the booking was parked. The
  // greeting decides what that is — same payload as the standalone path.
  if (verificationContinuation) {
    config.widgetUrl = withVerificationContinuation(config.widgetUrl, verificationContinuation)
    config.deferWidgetFrame = false
  }

  if (!config.enabled) return

  if (!config.widgetUrl) {
    console.warn("[assistant-widget] Missing data-business-slug, data-config-url, or data-widget-url.")
    return
  }

  if (document.querySelector("[data-public-assistant-widget-host]")) return

  const host = document.createElement("div")
  host.dataset.publicAssistantWidgetHost = "true"
  host.style.setProperty("--assistant-widget-font", config.fontFamily || ASSISTANT_WIDGET_DESIGN_FONT_FAMILY)
  document.body.appendChild(host)

  const root = host.attachShadow ? host.attachShadow({ mode: "open" }) : host
  root.innerHTML = `
    <style>
      ${fontImport(config)}
      :host, .assistant-widget-host {
        all: initial;
        color-scheme: light;
        font-family: var(--assistant-widget-font, "Nunito", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      .assistant-widget-host {
        box-sizing: border-box;
        max-width: 100%;
        position: fixed;
        right: 24px;
        bottom: 24px;
        z-index: 2147483000;
        /* Our green for the WhatsApp CTA (matches --assistant-frame-ok in the frame),
           never the raw brand green; a salon CTA color can override later. */
        --assistant-widget-whatsapp: #2f9e63;
      }
      .assistant-widget-host.is-bottom-left {
        right: auto;
        left: 24px;
      }
      .assistant-widget-host.is-middle-right,
      .assistant-widget-host.is-middle-left {
        bottom: auto;
        top: 50%;
        transform: translateY(-50%);
      }
      .assistant-widget-host.is-middle-left {
        right: auto;
        left: 0;
      }
      .assistant-widget-host.is-middle-right {
        right: 0;
      }
      .assistant-widget-launcher-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
      }
      .assistant-widget-host.is-open .assistant-widget-launcher-actions,
      .assistant-widget-host.is-closing .assistant-widget-launcher-actions {
        opacity: 0;
        pointer-events: none;
        visibility: hidden;
      }
      .assistant-widget-host.is-bottom-left .assistant-widget-launcher-actions {
        justify-content: flex-start;
      }
      .assistant-widget-host.is-middle-right .assistant-widget-launcher-actions,
      .assistant-widget-host.is-middle-left .assistant-widget-launcher-actions {
        align-items: stretch;
        flex-direction: column;
      }
      .assistant-widget-button {
        border: 1px solid rgba(255, 255, 255, 0.64);
        border-radius: 999px;
        background: var(--assistant-widget-secondary-accent);
        color: #fff;
        box-shadow:
          0 0 0 2px rgba(255, 255, 255, 0.18),
          0 1px 2px rgba(15, 23, 42, 0.22),
          0 6px 16px rgba(15, 23, 42, 0.2),
          0 14px 38px color-mix(in srgb, var(--assistant-widget-secondary-accent) 34%, transparent);
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        min-height: 56px;
        padding: 0 22px;
        font-family: var(--assistant-widget-font, "Nunito", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
        font-size: 16px;
        font-weight: 600;
        line-height: 1;
      }
      .assistant-widget-button.is-icon-only {
        width: 56px;
        min-width: 56px;
        padding: 0;
        justify-content: center;
      }
      .assistant-widget-host.is-middle-right .assistant-widget-button,
      .assistant-widget-host.is-middle-left .assistant-widget-button {
        border-radius: 12px 0 0 12px;
        box-shadow:
          0 0 0 2px rgba(255, 255, 255, 0.18),
          0 1px 2px rgba(15, 23, 42, 0.2),
          0 6px 14px rgba(15, 23, 42, 0.18),
          0 10px 28px color-mix(in srgb, var(--assistant-widget-secondary-accent) 32%, transparent);
        min-height: 54px;
        padding: 0 18px;
      }
      .assistant-widget-host.is-middle-left .assistant-widget-button {
        border-radius: 0 12px 12px 0;
      }
      .assistant-widget-host.is-middle-right .assistant-widget-button.is-icon-only,
      .assistant-widget-host.is-middle-left .assistant-widget-button.is-icon-only {
        width: 54px;
        min-width: 54px;
      }
      .assistant-widget-button svg {
        width: 22px;
        height: 22px;
        fill: currentColor;
      }
      .assistant-widget-contact-wrap {
        display: inline-flex;
      }
      .assistant-widget-contact-button {
        border: 1px solid rgba(255, 255, 255, 0.68);
        border-radius: 999px;
        background: var(--assistant-widget-secondary-accent);
        color: #fff;
        box-shadow:
          0 0 0 2px rgba(255, 255, 255, 0.2),
          0 1px 2px rgba(15, 23, 42, 0.22),
          0 6px 16px rgba(15, 23, 42, 0.2),
          0 14px 38px color-mix(in srgb, var(--assistant-widget-secondary-accent) 30%, transparent);
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        position: relative;
        text-decoration: none;
        transition: box-shadow 0.16s ease, filter 0.16s ease, transform 0.16s ease;
      }
      .assistant-widget-contact-button:hover,
      .assistant-widget-contact-button:focus-visible {
        filter: brightness(0.96) saturate(1.08);
        transform: translateY(-2px);
      }
      .assistant-widget-contact-button svg {
        width: 24px;
        height: 24px;
        fill: none;
        stroke: currentColor;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
      .assistant-widget-contact-button .assistant-widget-whatsapp-glyph {
        fill: currentColor;
        stroke: none;
      }
      .assistant-widget-contact-button--last-minute {
        background: #ffc233;
        border-color: rgba(255, 255, 255, 0.78);
        box-shadow:
          0 0 0 2px rgba(255, 255, 255, 0.24),
          0 1px 2px rgba(15, 23, 42, 0.2),
          0 6px 16px rgba(128, 82, 0, 0.2),
          0 14px 36px color-mix(in srgb, #ffc233 38%, transparent);
        color: #2e2112;
        justify-content: center;
        min-width: 56px;
        overflow: hidden;
        padding: 0 20px;
        width: auto;
      }
      .assistant-widget-contact-button--last-minute:hover,
      .assistant-widget-contact-button--last-minute:focus-visible {
        background: color-mix(in srgb, #ffc233 86%, #805200 14%);
        color: #2e2112;
      }
      .assistant-widget-contact-button--last-minute .assistant-widget-contact-button__label {
        display: inline-block;
        font-family: var(--assistant-widget-font, "Nunito", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
        font-size: 14px;
        font-weight: 800;
        line-height: 1.05;
        max-width: 220px;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .assistant-widget-contact-button[data-assistant-widget-contact-action="whatsapp"] {
        background: var(--assistant-widget-whatsapp);
        border-color: rgba(255, 255, 255, 0.72);
        box-shadow:
          0 0 0 2px rgba(255, 255, 255, 0.22),
          0 1px 2px rgba(15, 23, 42, 0.2),
          0 6px 16px rgba(13, 79, 45, 0.22),
          0 14px 36px color-mix(in srgb, var(--assistant-widget-whatsapp) 34%, transparent);
        color: #fff;
      }
      .assistant-widget-contact-button[data-assistant-widget-contact-action="whatsapp"]:hover,
      .assistant-widget-contact-button[data-assistant-widget-contact-action="whatsapp"]:focus-visible {
        background: color-mix(in srgb, var(--assistant-widget-whatsapp) 82%, #0a3d24 18%);
        border-color: rgba(255, 255, 255, 0.78);
        color: #fff;
      }
      .assistant-widget-button.is-pulsing,
      .assistant-widget-contact-button.is-pulsing {
        position: relative;
        z-index: 0;
      }
      .assistant-widget-button.is-pulsing::before,
      .assistant-widget-button.is-pulsing::after,
      .assistant-widget-contact-button.is-pulsing::before,
      .assistant-widget-contact-button.is-pulsing::after {
        content: "";
        position: absolute;
        inset: -3px;
        border-radius: inherit;
        border: 2.5px solid color-mix(in srgb, var(--assistant-widget-secondary-accent) 90%, #000 10%);
        opacity: 0.52;
        animation: assistant-widget-pulse-ring 4.5s cubic-bezier(0.33, 0.04, 0.26, 1) infinite;
        pointer-events: none;
        z-index: -1;
      }
      .assistant-widget-button.is-pulsing::after,
      .assistant-widget-contact-button.is-pulsing::after {
        animation-delay: 2.25s;
      }
      .assistant-widget-contact-button[data-assistant-widget-contact-action="whatsapp"].is-pulsing::before,
      .assistant-widget-contact-button[data-assistant-widget-contact-action="whatsapp"].is-pulsing::after {
        border-color: color-mix(in srgb, var(--assistant-widget-whatsapp) 90%, #000 10%);
      }
      @keyframes assistant-widget-pulse-ring {
        0% {
          transform: scale(0.99);
          opacity: 0.5;
        }
        20% {
          transform: scale(1.1);
          opacity: 0.44;
        }
        38% {
          transform: scale(1.32);
          opacity: 0.22;
        }
        50% {
          transform: scale(1.55);
          opacity: 0;
        }
        100% {
          transform: scale(2.08);
          opacity: 0;
        }
      }
      @keyframes assistant-widget-panel-open {
        from {
          transform: translateX(var(--assistant-widget-panel-closed-x, 100%));
        }
        to {
          transform: translateX(0);
        }
      }
      @keyframes assistant-widget-panel-close {
        from {
          transform: translateX(0);
        }
        to {
          transform: translateX(var(--assistant-widget-panel-closed-x, 100%));
        }
      }
      .assistant-widget-cta-stack {
        display: grid;
        width: 188px;
        border-radius: 12px 0 0 12px;
        overflow: hidden;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.12),
          0 2px 6px rgba(15, 23, 42, 0.08),
          0 12px 32px color-mix(in srgb, var(--assistant-widget-secondary-accent) 22%, rgba(15, 23, 42, 0.18) 78%);
        background: #fff;
        border: 1px solid color-mix(in srgb, var(--assistant-widget-secondary-accent) 94%, #000 6%);
        border-right: 0;
      }
      .assistant-widget-host.is-middle-left .assistant-widget-cta-stack {
        border-radius: 0 12px 12px 0;
        border-right: 1px solid color-mix(in srgb, var(--assistant-widget-secondary-accent) 94%, #000 6%);
        border-left: 0;
      }
      .assistant-widget-cta-title {
        background: var(--assistant-widget-secondary-accent);
        color: #fff;
        font-family: var(--assistant-widget-font, "Nunito", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
        font-size: 17px;
        font-weight: 700;
        line-height: 1;
        min-height: 46px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding: 0 14px;
        text-align: left;
      }
      .assistant-widget-cta {
        appearance: none;
        border: 0;
        border-top: 1px solid rgba(15, 23, 42, 0.14);
        background: #fff;
        color: var(--assistant-widget-secondary-accent);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        min-height: 52px;
        padding: 0 12px;
        text-decoration: none;
        text-align: left;
        font-family: var(--assistant-widget-font, "Nunito", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
        font-size: 17px;
        font-weight: 700;
        line-height: 1.1;
      }
      .assistant-widget-cta:hover,
      .assistant-widget-cta:focus-visible {
        background: #f8fafc;
      }
      .assistant-widget-cta--contact {
        justify-content: flex-start;
        gap: 10px;
        padding: 0 14px;
        text-align: left;
      }
      .assistant-widget-cta--contact.is-whatsapp {
        color: var(--assistant-widget-whatsapp);
      }
      .assistant-widget-cta--contact.is-whatsapp .assistant-widget-cta__icon {
        color: var(--assistant-widget-whatsapp);
      }
      .assistant-widget-cta__icon {
        color: var(--assistant-widget-secondary-accent);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        height: 18px;
        width: 18px;
      }
      .assistant-widget-cta__icon svg {
        height: 18px;
        width: 18px;
      }
      .assistant-widget-cta__label {
        flex: 1 1 auto;
      }
      /* The open panel docks to the same side as the launcher: right for the
         default bottom-right (and middle-right), left for the -left positions. */
      .assistant-widget-frame-wrap {
        bottom: 0;
        height: 100vh;
        height: 100dvh;
        isolation: isolate;
        left: auto;
        right: 0;
        pointer-events: none;
        position: fixed;
        top: 0;
        visibility: hidden;
        width: min(var(--assistant-widget-panel-width-desktop, 500px), 100vw);
        width: min(var(--assistant-widget-panel-width-desktop, 500px), 100dvw);
        z-index: 2147483000;
      }
      .assistant-widget-host.is-bottom-left .assistant-widget-frame-wrap,
      .assistant-widget-host.is-middle-left .assistant-widget-frame-wrap {
        left: 0;
        right: auto;
      }
      .assistant-widget-host.is-open .assistant-widget-frame-wrap,
      .assistant-widget-host.is-closing .assistant-widget-frame-wrap {
        visibility: visible;
        pointer-events: auto;
      }
      .assistant-widget-host.is-closing .assistant-widget-frame-wrap {
        pointer-events: none;
      }
      .assistant-widget-host.is-middle-right .assistant-widget-frame-wrap,
      .assistant-widget-host.is-middle-left .assistant-widget-frame-wrap {
        bottom: 0;
        margin-bottom: 0;
        top: 0;
        transform: none;
      }
      .assistant-widget-host.is-middle-right .assistant-widget-frame-wrap {
        left: auto;
        right: 0;
      }
      .assistant-widget-host.is-middle-left .assistant-widget-frame-wrap {
        left: 0;
        right: auto;
      }
      .assistant-widget-frame-surface {
        border-radius: 0;
        overflow: hidden;
        box-shadow:
          0 1px 6px rgba(0, 0, 0, 0.06),
          0 2px 32px rgba(0, 0, 0, 0.16);
        background: #fff;
        transform-origin: 0 50%;
        transform: translateX(var(--assistant-widget-panel-closed-x, 100%));
        position: absolute;
        inset: 0;
        width: auto;
        height: auto;
        z-index: 2;
      }
      .assistant-widget-host.is-bottom-left .assistant-widget-frame-surface {
        --assistant-widget-panel-closed-x: -100%;
        transform-origin: 28px 100%;
      }
      .assistant-widget-host.is-middle-right .assistant-widget-frame-surface {
        transform-origin: calc(100% - 20px) 50%;
      }
      .assistant-widget-host.is-middle-left .assistant-widget-frame-surface {
        --assistant-widget-panel-closed-x: -100%;
        transform-origin: 28px 50%;
      }
      .assistant-widget-host.is-open .assistant-widget-frame-surface {
        animation: assistant-widget-panel-open 0.8s ease forwards;
      }
      .assistant-widget-host.is-closing .assistant-widget-frame-surface {
        animation: assistant-widget-panel-close 0.8s ease forwards;
      }
      .assistant-widget-frame {
        border: 0;
        width: 100%;
        height: 100%;
        display: block;
        background: #fff;
      }
      @media (max-width: 900px) {
        .assistant-widget-frame-wrap {
          width: min(var(--assistant-widget-panel-width-tablet, 500px), 100vw);
          width: min(var(--assistant-widget-panel-width-tablet, 500px), 100dvw);
        }
      }
      @media (max-width: 520px) {
        .assistant-widget-host,
        .assistant-widget-host.is-bottom-left,
        .assistant-widget-host.is-middle-right,
        .assistant-widget-host.is-middle-left {
          right: 12px;
          left: 12px;
          bottom: 12px;
          max-width: calc(100vw - 24px);
          max-width: calc(100dvw - 24px);
          top: auto;
          transform: none;
        }
        .assistant-widget-host.is-middle-right .assistant-widget-frame-wrap,
        .assistant-widget-host.is-middle-left .assistant-widget-frame-wrap {
          position: fixed;
          transform: none;
          margin-bottom: 0;
          right: auto;
          left: 0;
          bottom: 0;
          top: 0;
        }
        .assistant-widget-frame-wrap {
          height: 100vh;
          height: 100dvh;
          position: fixed;
          width: min(var(--assistant-widget-panel-width-mobile, 100%), 100vw);
          width: min(var(--assistant-widget-panel-width-mobile, 100%), 100dvw);
        }
        .assistant-widget-frame-surface {
          border-radius: 16px;
          transform-origin: 50% 100%;
        }
        .assistant-widget-button {
          max-width: 100%;
          min-width: 56px;
          min-height: 52px;
          justify-content: center;
          float: right;
          padding: 0 16px;
        }
        .assistant-widget-button span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .assistant-widget-contact-button {
          flex: 0 0 48px;
          height: 48px;
          width: 48px;
        }
        .assistant-widget-contact-button--last-minute {
          flex: 1 1 auto;
          max-width: none;
          min-width: 0;
          padding: 0 12px;
          width: auto;
        }
        .assistant-widget-contact-button--last-minute .assistant-widget-contact-button__label {
          max-width: none;
          text-align: center;
          white-space: normal;
        }
        .assistant-widget-launcher-actions {
          gap: 8px;
          justify-content: flex-end;
          flex-wrap: wrap;
          max-width: 100%;
        }
        .assistant-widget-cta-stack {
          width: auto;
          max-width: 100%;
          grid-template-columns: 1fr 1fr;
          border-radius: 16px;
          border: 1px solid color-mix(in srgb, var(--assistant-widget-secondary-accent) 94%, #000 6%);
          box-shadow:
            0 1px 2px rgba(15, 23, 42, 0.1),
            0 10px 28px color-mix(in srgb, var(--assistant-widget-secondary-accent) 18%, rgba(15, 23, 42, 0.12) 82%);
        }
        .assistant-widget-cta-title {
          grid-column: 1 / -1;
          min-height: 40px;
          font-size: 15px;
        }
        .assistant-widget-cta {
          min-height: 48px;
          font-size: 15px;
        }
        .assistant-widget-cta-stack__contacts {
          grid-column: 1 / -1;
        }
        .assistant-widget-host.is-open,
        .assistant-widget-host.is-closing {
          right: 0;
          bottom: 0;
          left: 0;
          top: 0;
          max-width: none;
          width: 100vw;
          width: 100dvw;
        }
        .assistant-widget-host.is-open .assistant-widget-frame-wrap,
        .assistant-widget-host.is-closing .assistant-widget-frame-wrap,
        .assistant-widget-host.is-open.is-middle-right .assistant-widget-frame-wrap,
        .assistant-widget-host.is-closing.is-middle-right .assistant-widget-frame-wrap,
        .assistant-widget-host.is-open.is-middle-left .assistant-widget-frame-wrap,
        .assistant-widget-host.is-closing.is-middle-left .assistant-widget-frame-wrap {
          bottom: auto;
          height: 100vh;
          height: 100dvh;
          left: 0;
          margin-bottom: 0;
          position: fixed;
          right: 0;
          top: 0;
          transform: none;
          max-width: none;
          width: 100vw;
          width: 100dvw;
        }
        .assistant-widget-host.is-open .assistant-widget-frame-surface,
        .assistant-widget-host.is-closing .assistant-widget-frame-surface {
          border-radius: 0;
        }
      }
      @media (max-width: 520px) {
        .assistant-widget-host--force-desktop.assistant-widget-host.is-middle-right,
        .assistant-widget-host--force-desktop.assistant-widget-host.is-middle-left {
          right: auto;
          bottom: auto;
          top: 50%;
          transform: translateY(-50%);
          left: auto;
        }
        .assistant-widget-host--force-desktop.assistant-widget-host.is-middle-left {
          left: 0;
        }
        .assistant-widget-host--force-desktop.assistant-widget-host.is-middle-right {
          right: 0;
        }
        .assistant-widget-host--force-desktop.assistant-widget-host:not(.is-middle-right):not(.is-middle-left) {
          right: 24px;
          left: auto;
          bottom: 24px;
          top: auto;
          transform: none;
        }
        .assistant-widget-host--force-desktop.assistant-widget-host.is-bottom-left:not(.is-middle-right):not(.is-middle-left) {
          left: 24px;
          right: auto;
        }
        .assistant-widget-host--force-desktop.assistant-widget-host.is-middle-right .assistant-widget-frame-wrap,
        .assistant-widget-host--force-desktop.assistant-widget-host.is-middle-left .assistant-widget-frame-wrap {
          bottom: 0;
          margin-bottom: 0;
          top: 0;
          transform: none;
        }
        .assistant-widget-host--force-desktop.assistant-widget-host.is-middle-right .assistant-widget-frame-wrap {
          left: auto;
          right: 0;
        }
        .assistant-widget-host--force-desktop.assistant-widget-host.is-middle-left .assistant-widget-frame-wrap {
          left: 0;
          right: auto;
        }
        .assistant-widget-host--force-desktop .assistant-widget-frame-wrap {
          height: 100vh;
          height: 100dvh;
          position: fixed;
          width: min(var(--assistant-widget-panel-width-desktop, 500px), 100vw);
          width: min(var(--assistant-widget-panel-width-desktop, 500px), 100dvw);
        }
        .assistant-widget-host--force-desktop .assistant-widget-frame-surface {
          border-radius: 0;
        }
        .assistant-widget-host--force-desktop.is-middle-right .assistant-widget-frame-surface {
          transform-origin: calc(100% - 20px) 50%;
        }
        .assistant-widget-host--force-desktop.is-middle-left .assistant-widget-frame-surface {
          transform-origin: 28px 50%;
        }
        .assistant-widget-host--force-desktop .assistant-widget-cta-stack {
          width: 188px;
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          border-radius: 12px 0 0 12px;
          border: 1px solid color-mix(in srgb, var(--assistant-widget-secondary-accent) 94%, #000 6%);
          border-right: 0;
          box-shadow:
            0 1px 2px rgba(15, 23, 42, 0.12),
            0 2px 6px rgba(15, 23, 42, 0.08),
            0 12px 32px color-mix(in srgb, var(--assistant-widget-secondary-accent) 22%, rgba(15, 23, 42, 0.18) 78%);
        }
        .assistant-widget-host--force-desktop.is-middle-left .assistant-widget-cta-stack {
          border-radius: 0 12px 12px 0;
          border-right: 1px solid color-mix(in srgb, var(--assistant-widget-secondary-accent) 94%, #000 6%);
          border-left: 0;
        }
        .assistant-widget-host--force-desktop .assistant-widget-cta-title {
          grid-column: auto;
          min-height: 46px;
          font-size: 17px;
          justify-content: flex-start;
          text-align: left;
        }
        .assistant-widget-host--force-desktop .assistant-widget-cta {
          min-height: 52px;
          font-size: 17px;
          justify-content: flex-start;
          text-align: left;
        }
        .assistant-widget-host--force-desktop .assistant-widget-cta-stack__contacts {
          grid-column: auto;
        }
        .assistant-widget-host--force-desktop .assistant-widget-launcher-actions {
          justify-content: flex-end;
          flex-wrap: nowrap;
        }
      }
      .assistant-widget-host--setup-preview {
        zoom: 1;
      }
      .assistant-widget-host--setup-preview .assistant-widget-frame-surface {
        border: 1px solid color-mix(in srgb, var(--assistant-widget-secondary-accent) 36%, transparent);
        border-radius: 16px;
        box-shadow:
          0 1px 2px color-mix(in srgb, var(--assistant-widget-secondary-accent) 10%, transparent),
          0 12px 32px color-mix(in srgb, var(--assistant-widget-secondary-accent) 18%, transparent);
      }
      .assistant-widget-host--setup-preview .assistant-widget-launcher-actions {
        display: none;
      }
      @media (max-width: 520px) {
        .assistant-widget-host--setup-preview {
          zoom: 1;
        }
      }
      @media (max-height: 640px) {
        .assistant-widget-host--setup-preview {
          zoom: 1;
        }
      }
    </style>
    <div class="assistant-widget-host ${positionClass(config.position)}${config.forceDesktopBreakpoint ? " assistant-widget-host--force-desktop" : ""}${config.setupPreview ? " assistant-widget-host--setup-preview" : ""}" style="--assistant-widget-accent: ${escapeAttribute(config.accent)}; --assistant-widget-secondary-accent: ${escapeAttribute(config.secondaryAccent)}; --assistant-widget-font: ${escapeAttribute(config.fontFamily)}; --assistant-widget-panel-width-desktop: ${escapeAttribute(config.panelWidthDesktop)}; --assistant-widget-panel-width-tablet: ${escapeAttribute(config.panelWidthTablet)}; --assistant-widget-panel-width-mobile: ${escapeAttribute(config.panelWidthMobile)}">
      <div class="assistant-widget-frame-wrap">
        <div class="assistant-widget-frame-surface">
          <iframe class="assistant-widget-frame" title="Digital receptionist" loading="lazy" ${
            config.deferWidgetFrame
              ? `src="about:blank" data-assistant-widget-frame-src="${escapeAttribute(config.widgetUrl)}"`
              : `src="${escapeAttribute(config.widgetUrl)}"`
          }></iframe>
        </div>
      </div>
      ${launcherMarkup(config)}
    </div>
  `

  const shell = root.querySelector(".assistant-widget-host")
  const frame = root.querySelector(".assistant-widget-frame")
  const chatButtons = root.querySelectorAll("[data-assistant-widget-open]")
  const PANEL_ANIM_MS = 800
  let panelCloseTimer = null
  let pageScrollLock = null

  function trackWidgetEvent(eventName, { openingAction = "", outcome = "", metadata = {} } = {}) {
    if (!config.analyticsUrl || !config.businessId || !eventName) return

    const touchpointSessionId = touchpointSessionIdFromConfig(config)
    const body = {
      business_id: config.businessId,
      branch_id: config.branchId || "",
      visitor_id: touchpointSessionId,
      event_name: eventName,
      occurred_at: new Date().toISOString(),
      parent_url: window.location.href,
      opening_action: openingAction,
      outcome,
      test_mode: config.testMode,
      landing_url: window.location.href,
      page_title: document.title || "",
      referrer: document.referrer || "",
      touchpoint_session_id: touchpointSessionId,
      touchpoint_source: "website_widget",
      touchpoint_version: touchpointVersionFromConfig(config),
      service_discovery_strategy: config.serviceDiscoveryStrategy || "",
      service_discovery_experiment_key: config.serviceDiscoveryExperimentKey || "",
      service_discovery_experiment_variant: config.serviceDiscoveryExperimentVariant || "",
      metadata,
    }

    fetch(config.analyticsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(config.assistantSessionToken ? { "X-Assistant-Session": config.assistantSessionToken } : {}),
      },
      body: JSON.stringify(body),
      credentials: "omit",
      keepalive: true,
    }).catch((error) => {
      console.debug("[assistant-widget] analytics event failed", error)
    })
  }

  function shouldLockPageScroll() {
    // The iframe owns the transcript scroll while the panel is open. Leaving
    // the host document scrollable on desktop exposes two parallel scrollbars
    // and allows the page behind the assistant to move independently.
    return (
      shell.classList.contains("is-open") ||
      shell.classList.contains("is-closing")
    )
  }

  // `overflow: hidden` alone does NOT stop iOS Safari scrolling the page behind
  // the panel — it only suppresses the desktop scrollbar. On iPhone the salon
  // page kept scrolling under the open widget, most visibly once the keyboard
  // pushed a focused input into view (Sonja, 2026-08-19). Pinning the body with
  // `position: fixed` at a negative offset is the technique that actually holds,
  // so the saved scroll position has to be restored by hand on release —
  // otherwise closing the widget drops the visitor at the top of the page.
  function updatePageScrollLock() {
    const locked = shouldLockPageScroll()
    if (locked && !pageScrollLock) {
      const body = document.body
      pageScrollLock = {
        scrollY: window.scrollY || window.pageYOffset || 0,
        documentOverflow: document.documentElement.style.overflow,
        bodyOverflow: body?.style.overflow || "",
        bodyPosition: body?.style.position || "",
        bodyTop: body?.style.top || "",
        bodyLeft: body?.style.left || "",
        bodyRight: body?.style.right || "",
        bodyWidth: body?.style.width || "",
      }
      document.documentElement.style.overflow = "hidden"
      if (body) {
        body.style.overflow = "hidden"
        body.style.position = "fixed"
        body.style.top = `-${pageScrollLock.scrollY}px`
        body.style.left = "0"
        body.style.right = "0"
        body.style.width = "100%"
      }
    } else if (!locked && pageScrollLock) {
      const body = document.body
      document.documentElement.style.overflow = pageScrollLock.documentOverflow
      if (body) {
        body.style.overflow = pageScrollLock.bodyOverflow
        body.style.position = pageScrollLock.bodyPosition
        body.style.top = pageScrollLock.bodyTop
        body.style.left = pageScrollLock.bodyLeft
        body.style.right = pageScrollLock.bodyRight
        body.style.width = pageScrollLock.bodyWidth
      }
      const restoreTo = pageScrollLock.scrollY
      pageScrollLock = null
      window.scrollTo(0, restoreTo)
    }
  }

  function assistantFrameUrlForOpeningAction(openingAction = "") {
    const value = String(config.widgetUrl || "").trim()
    const action = String(openingAction || "").trim()

    try {
      if (action === "last_minute.offers") {
        const nextUrl = new URL(value, window.location.href)
        nextUrl.searchParams.set("last_minute_home_refresh", String(Date.now()))
        return nextUrl.toString()
      }
      if (action === "contact.overview") {
        const nextUrl = new URL(value, window.location.href)
        nextUrl.searchParams.set("initial_assistant_action", action)
        return nextUrl.toString()
      }
      return value
    } catch {
      return value
    }
  }

  function postAssistantFrameMessage(message, frameUrl = "") {
    const targetUrl = String(frameUrl || frame?.getAttribute("src") || config.widgetUrl || "").trim()
    if (!targetUrl || !frame?.contentWindow) return

    try {
      const targetOrigin = assistantFrameMessageTargetOrigin(targetUrl)
      if (!targetOrigin) return

      frame.contentWindow.postMessage(message, targetOrigin)
    } catch {
      // An invalid configured widget URL cannot be a safe postMessage target.
    }
  }

  function openAssistantPanel(openingAction = "") {
    if (panelCloseTimer) {
      clearTimeout(panelCloseTimer)
      panelCloseTimer = null
    }
    shell.classList.remove("is-closing")
    const openingFrameUrl = assistantFrameUrlForOpeningAction(openingAction)
    if (config.deferWidgetFrame && frame) {
      const pendingSrc = frame.getAttribute("data-assistant-widget-frame-src")
      if (pendingSrc) {
        const currentSrc = frame.getAttribute("src") || ""
        if (!currentSrc || currentSrc === "about:blank") {
          frame.setAttribute("src", openingFrameUrl || pendingSrc)
        }
      }
    } else if (openingAction === "last_minute.offers" && frame && openingFrameUrl) {
      frame.setAttribute("src", openingFrameUrl)
    }
    // Contact opens as a frame page: an already-booted frame navigates via
    // message; a deferred boot carries initial_assistant_action in its src.
    if (openingAction === "contact.overview") {
      postAssistantFrameMessage({ type: "public-assistant-widget:assistant-action", action: "contact.overview" }, openingFrameUrl)
    }
    shell.classList.add("is-open")
    chatButtons.forEach((target) => target.setAttribute("aria-expanded", "true"))
    updatePageScrollLock()
    postAssistantFrameMessage({ type: "public-assistant-widget:track", eventName: "widget_opened" }, openingFrameUrl)
  }

  function closeAssistantPanel() {
    if (!shell.classList.contains("is-open") && !shell.classList.contains("is-closing")) return
    if (shell.classList.contains("is-closing")) return
    shell.classList.remove("is-open")
    shell.classList.add("is-closing")
    chatButtons.forEach((target) => target.setAttribute("aria-expanded", "false"))
    updatePageScrollLock()
    // The iframe remains mounted. Closing is only a visual dismissal, so a
    // normal reopen resumes its current server-issued page and history.
    // Explicit Home and special opening actions remain the intentional ways
    // to restart or navigate the assistant.
    panelCloseTimer = setTimeout(() => {
      panelCloseTimer = null
      shell.classList.remove("is-closing")
      updatePageScrollLock()
    }, PANEL_ANIM_MS)
  }

  function handleWidgetOutsideClick(event) {
    if (!shell.classList.contains("is-open")) return
    if (assistantWidgetClickIsOutside(event, host)) closeAssistantPanel()
  }

  function trackLastMinuteOpeningActionClick() {
    postAssistantFrameMessage({
      type: "public-assistant-widget:track",
      eventName: "opening_action_clicked",
      openingAction: "last_minute.offers",
    })
  }

  function openLastMinuteAssistantPanel() {
    openAssistantPanel("last_minute.offers")
    trackLastMinuteOpeningActionClick()
  }

  function openContactAssistantPanel() {
    openAssistantPanel("contact.overview")
  }

  function handleExternalLastMinuteLinkClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target instanceof Element ? event.target.closest("a[href]") : null
    if (!isSameSiteLastMinuteLink(link)) return

    event.preventDefault()
    event.stopImmediatePropagation?.()
    event.stopPropagation()
    openLastMinuteAssistantPanel()
  }

  chatButtons.forEach((button) => button.addEventListener("click", () => {
    if (shell.classList.contains("is-open")) {
      closeAssistantPanel()
    } else {
      const openingAction = String(button.dataset.assistantWidgetOpenAction || "").trim()
      openAssistantPanel(openingAction)
      if (openingAction === "last_minute.offers") {
        trackLastMinuteOpeningActionClick()
      }
    }
  }))

  // WARM ON INTENT. The live frame is a lazy iframe in a hidden panel. Chromium
  // usually loads it early anyway (the panel sits beside the viewport; checked
  // 2026-09 in headless Chromium), but where a browser or layout really defers
  // it, the widget document, its bundle and Home would start only after the
  // click. A pointer entering, a keyboard focus or a touch starting on the
  // launcher is intent: switch the iframe to eager then, so the work runs in
  // the few hundred milliseconds before the click lands. Not for a launcher
  // whose click loads a different frame URL (Last-Minute): that would be wasted.
  function warmAssistantFrame() {
    if (!frame || config.deferWidgetFrame) return
    if (frame.getAttribute("loading") === "eager") return
    frame.setAttribute("loading", "eager")
  }
  chatButtons.forEach((button) => {
    const openingAction = String(button.dataset.assistantWidgetOpenAction || "").trim()
    if (openingAction === "last_minute.offers") return
    ;["pointerenter", "focus", "touchstart"].forEach((eventName) => {
      button.addEventListener(eventName, warmAssistantFrame, { once: true, passive: true })
    })
  })

  document.addEventListener("click", handleExternalLastMinuteLinkClick, true)
  // The panel lives in a cross-origin iframe, so the frame cannot observe
  // clicks on the website behind it. Capture the host-page click here and
  // close only when the composed path does not pass through our shadow host.
  document.addEventListener("click", handleWidgetOutsideClick, true)
  if (verificationContinuation) {
    window.setTimeout(() => openAssistantPanel(), 0)
  } else if (shouldOpenLastMinuteFromUrl()) {
    window.setTimeout(() => openLastMinuteAssistantPanel(), 0)
  } else if (config.setupPreview) {
    window.setTimeout(() => openAssistantPanel(), 0)
  }

  root.querySelectorAll("[data-assistant-widget-booking]").forEach((link) => {
    // The click is recorded; `booking_started` is not. It is server-only
    // (JourneyCatalog ACTION_STAGES["booking.start"]), and a second row here
    // for the same intent put a conversation-less session into every
    // booking-intent count.
    link.addEventListener("click", () => {
      trackWidgetEvent("opening_action_clicked", { openingAction: "booking.start" })
    })
  })

  root.querySelectorAll("[data-assistant-widget-contact-action]").forEach((link) => {
    link.addEventListener("click", (event) => {
      const contactAction = link.dataset.assistantWidgetContactAction
      const metadata = contactActionMetadata(link, contactAction)
      // Call/WhatsApp with an in-widget page: open the frame on the contact
      // page instead of an embed-side popover — one consistent surface. The
      // contact_started event fires from the frame rows there.
      if (link.dataset.assistantWidgetContactFrame === "true") {
        event.preventDefault()
        openContactAssistantPanel()
      }
      trackWidgetEvent("opening_action_clicked", {
        openingAction: `contact.${contactAction}`,
        metadata,
      })
      if (link.dataset.assistantWidgetContactFrame !== "true") {
        trackWidgetEvent("contact_started", {
          openingAction: `contact.${contactAction}`,
          metadata,
        })
      }
    })
  })

  // A pinned body must never outlive the page. Without this, leaving the site
  // with the panel open and coming back through the bfcache restores a document
  // whose body is still `position: fixed` — the salon page then looks frozen
  // and the visitor has no widget left to close.
  window.addEventListener("pagehide", () => {
    if (!pageScrollLock) return

    const body = document.body
    document.documentElement.style.overflow = pageScrollLock.documentOverflow
    if (body) {
      body.style.overflow = pageScrollLock.bodyOverflow
      body.style.position = pageScrollLock.bodyPosition
      body.style.top = pageScrollLock.bodyTop
      body.style.left = pageScrollLock.bodyLeft
      body.style.right = pageScrollLock.bodyRight
      body.style.width = pageScrollLock.bodyWidth
    }
    pageScrollLock = null
  })

  window.addEventListener("message", (event) => {
    if (!trustedAssistantFrameMessage(frame?.contentWindow, config.widgetUrl, event)) return

    if (event.data?.type === "public-assistant-widget:close") {
      closeAssistantPanel()
      return
    }

    if (event.data?.type === "public-assistant-widget:redirect" && event.data?.url) {
      window.location.assign(event.data.url)
    }
  })
}

// Kept in sync with Public::AssistantController::EMBEDDED_VERIFICATION_FRAGMENT_KEY.
const ASSISTANT_VERIFICATION_FRAGMENT_KEY = "assistant-verification"

// Reads the continuation once and strips it from the address bar immediately,
// so it does not survive in history, a copied URL, or a screenshot. A fragment
// is never sent to the salon's server, so it stays out of their access logs
// and off any Referer header.
function takeVerificationContinuationFromUrl() {
  try {
    const hash = String(window.location.hash || "").replace(/^#/, "")
    if (!hash) return ""

    const params = new URLSearchParams(hash)
    const token = String(params.get(ASSISTANT_VERIFICATION_FRAGMENT_KEY) || "").trim()
    if (!token) return ""

    params.delete(ASSISTANT_VERIFICATION_FRAGMENT_KEY)
    const remaining = params.toString()
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}${remaining ? `#${remaining}` : ""}`
    )
    return token
  } catch {
    return ""
  }
}

function withVerificationContinuation(url, token) {
  const value = String(url || "").trim()
  if (!value || !token) return value

  try {
    const nextUrl = new URL(value, window.location.href)
    nextUrl.searchParams.set("verification_continuation_token", token)
    return nextUrl.toString()
  } catch {
    return value
  }
}

function withAssistantFrameMode(url, mode) {
  const value = String(url || "").trim()
  if (!value) return value

  try {
    const nextUrl = new URL(value, window.location.href)
    nextUrl.searchParams.set("frame_mode", mode)
    return nextUrl.toString()
  } catch {
    return value
  }
}

function contactActionMetadata(link, contactAction) {
  return {
    contact_action: contactAction,
    contact_module: link.dataset.assistantWidgetContactModule || "",
    contact_destination: link.dataset.assistantWidgetContactDestination || "",
    href: link.getAttribute("href") || "",

    label: link.getAttribute("aria-label") || link.getAttribute("title") || link.textContent?.trim() || "",
  }
}

function touchpointSessionIdFromConfig(config) {
  return safeUrl(config.widgetUrl)?.searchParams.get("touchpoint_session_id") || ""
}

function touchpointVersionFromConfig(config) {
  return safeUrl(config.widgetUrl)?.searchParams.get("touchpoint_version") || "draft_v1"
}

function safeUrl(value) {
  try {
    return new URL(value)
  } catch {
    return null
  }
}

async function loadRemoteAssistantWidgetConfig(config) {
  if (!config.configUrl) return config

  try {
    const response = await fetch(config.configUrl, {
      cache: "no-store",
      credentials: "omit",
      headers: { Accept: "application/json" },
    })

    if (response.status === 403 || response.status === 404) {
      return { ...config, enabled: false }
    }
    if (!response.ok) return config

    return mergeRemoteAssistantWidgetConfig(config, await response.json())
  } catch (error) {
    console.warn("[assistant-widget] Could not load remote widget config.", error)
    return config
  }
}

function fontImport(config) {
  if (!config.fontStylesheet) return ""

  return `@import url("${escapeCssUrl(config.fontStylesheet)}");`
}

function escapeCssUrl(value) {
  return String(value).replace(/["\\\n\r\f]/g, "")
}

function launcherMarkup(config) {
  const contactButtons = contactLauncherMarkup(config)
  const chatLauncherOn = config.chatLauncherEnabled === true
  const lastMinuteButton = lastMinuteLauncherMarkup(config)

  if (config.layout === "cta-stack") {
    const contactStackEntries = contactStackMarkup(config)
    const contactButtonsMarkup = contactStackEntries || ""
    const lastMinuteRow = lastMinuteStackMarkup(config)
    const bookingRow = config.bookingLauncherEnabled === false
      ? ""
      : `<a class="assistant-widget-cta" href="${escapeAttribute(config.bookingUrl)}" target="_blank" rel="noopener" data-assistant-widget-booking>${escapeHtml(config.bookingLabel)}</a>`
    const chatRow = chatLauncherOn
      ? `<button class="assistant-widget-cta${config.chatPulse ? " is-pulsing" : ""}" type="button" aria-expanded="false" data-assistant-widget-open>${escapeHtml(config.chatLabel)}</button>`
      : ""
    return `
      <div class="assistant-widget-launcher-actions">
        <div class="assistant-widget-cta-stack">
          <div class="assistant-widget-cta-title">${escapeHtml(config.panelTitle)}</div>
          ${bookingRow}
          ${lastMinuteRow}
          ${chatRow}
          ${contactButtonsMarkup}
        </div>
      </div>
    `
  }

  const buttonClass = [
    "assistant-widget-button",
    config.labelVisible ? "" : "is-icon-only",
    config.chatPulse ? "is-pulsing" : "",
  ].filter(Boolean).join(" ")
  const labelMarkup = config.labelVisible ? `<span>${escapeHtml(config.buttonLabel)}</span>` : ""
  const chatButton = chatLauncherOn
    ? `<button class="${buttonClass}" type="button" aria-expanded="false" aria-label="${escapeAttribute(config.buttonLabel)}" data-assistant-widget-open>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9.4L5 20.3V17H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm2.5 5.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Zm5.5 0a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Zm5.5 0a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Z"/></svg>
        ${labelMarkup}
      </button>`
    : ""

  return `
    <div class="assistant-widget-launcher-actions">
      ${contactButtons}
      ${lastMinuteButton}
      ${chatButton}
    </div>
  `
}

// Call with a location choice or WhatsApp: these open the widget frame on
// its contact page (one consistent surface) instead of an embed-side panel.
function contactActionOpensAssistant(action) {
  return (action.key === "call" && action.locations?.length > 0) || (action.key === "whatsapp" && action.url)
}

function contactLauncherMarkup(config) {
  return (config.contactActions || []).map((action) => {
    const label = action.label || action.key
    const pulseClass = action.pulse ? " is-pulsing" : ""
    if (contactActionOpensAssistant(action)) {
      return `
        <div class="assistant-widget-contact-wrap">
          <button class="assistant-widget-contact-button${pulseClass}" type="button" aria-label="${escapeAttribute(label)}" title="${escapeAttribute(label)}" data-assistant-widget-contact-frame="true" data-assistant-widget-contact-action="${escapeAttribute(action.key)}">
            ${contactIcon(action.key)}
          </button>
        </div>
      `
    }

    return `
      <a class="assistant-widget-contact-button${pulseClass}" href="${escapeAttribute(action.url)}" target="_blank" rel="noopener" aria-label="${escapeAttribute(label)}" title="${escapeAttribute(label)}" data-assistant-widget-contact-action="${escapeAttribute(action.key)}" data-assistant-widget-contact-module="launcher">
        ${contactIcon(action.key)}
      </a>
    `
  }).join("")
}

function lastMinuteLauncherConfig(config) {
  const action = config.lastMinuteLauncher || {}
  if (action.enabled !== true || action.action !== "last_minute.offers") return null

  return {
    label: String(action.label || "Last-minute appointments"),
    pulse: action.pulse === true,
    action: "last_minute.offers",
  }
}

function isSameSiteLastMinuteLink(link) {
  if (!link) return false

  try {
    const url = new URL(link.getAttribute("href") || "", window.location.href)
    const current = new URL(window.location.href)
    const pathname = url.pathname.replace(/\/+$/, "") || "/"
    return url.origin === current.origin && pathname === "/lastminute"
  } catch {
    return false
  }
}

function shouldOpenLastMinuteFromUrl() {
  try {
    const url = new URL(window.location.href)
    const assistantAction = String(url.searchParams.get("assistant_action") || "").trim()
    const lastMinute = String(url.searchParams.get("last_minute") || "").trim().toLowerCase()
    const hash = url.hash.replace(/^#/, "").trim().toLowerCase()

    return assistantAction === "last_minute" ||
      assistantAction === "last_minute.offers" ||
      ["1", "true", "yes", "last_minute", "last-minute"].includes(lastMinute) ||
      ["lastminute", "last-minute", "last_minute"].includes(hash)
  } catch {
    return false
  }
}

function lastMinuteLauncherMarkup(config) {
  const action = lastMinuteLauncherConfig(config)
  if (!action) return ""

  const pulseClass = action.pulse ? " is-pulsing" : ""
  return `
    <button class="assistant-widget-contact-button assistant-widget-contact-button--last-minute${pulseClass}" type="button" aria-expanded="false" aria-label="${escapeAttribute(action.label)}" title="${escapeAttribute(action.label)}" data-assistant-widget-open data-assistant-widget-open-action="${escapeAttribute(action.action)}">
      <span class="assistant-widget-contact-button__label">${escapeHtml(action.label)}</span>
    </button>
  `
}

function lastMinuteStackMarkup(config) {
  const action = lastMinuteLauncherConfig(config)
  if (!action) return ""

  const pulseClass = action.pulse ? " is-pulsing" : ""
  return `
    <button class="assistant-widget-cta${pulseClass}" type="button" aria-expanded="false" aria-label="${escapeAttribute(action.label)}" data-assistant-widget-open data-assistant-widget-open-action="${escapeAttribute(action.action)}">
      <span class="assistant-widget-cta__label">${escapeHtml(action.label)}</span>
    </button>
  `
}

function contactStackMarkup(config) {
  return (config.contactActions || []).map((action) => {
    const label = action.label || action.key
    const pulseClass = action.pulse ? " is-pulsing" : ""
    const icon = `<span class="assistant-widget-cta__icon" aria-hidden="true">${contactIcon(action.key)}</span>`
    const content = `${icon}<span class="assistant-widget-cta__label">${escapeHtml(label)}</span>`

    if (contactActionOpensAssistant(action)) {
      return `
        <button class="assistant-widget-cta assistant-widget-cta--contact${pulseClass}${action.key === "whatsapp" ? " is-whatsapp" : ""}" type="button" aria-label="${escapeAttribute(label)}" data-assistant-widget-contact-frame="true" data-assistant-widget-contact-action="${escapeAttribute(action.key)}">
          ${content}
        </button>
      `
    }

    return `
      <a class="assistant-widget-cta assistant-widget-cta--contact${pulseClass}${action.key === "whatsapp" ? " is-whatsapp" : ""}" href="${escapeAttribute(action.url)}" target="_blank" rel="noopener" aria-label="${escapeAttribute(label)}" data-assistant-widget-contact-action="${escapeAttribute(action.key)}" data-assistant-widget-contact-module="cta_stack">
        ${content}
      </a>
    `
  }).join("")
}

function contactIcon(key) {
  switch (key) {
    case "last_minute":
      return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 10 10A10.01 10.01 0 0 0 12 2Zm1 10.2 3.2 1.9a1 1 0 0 1-1 1.8l-3.7-2.2A1 1 0 0 1 11 13V7.5a1 1 0 0 1 2 0v4.7Z"></path></svg>'
    case "sms":
      return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z"></path></svg>'
    case "whatsapp":
      return '<svg class="assistant-widget-whatsapp-glyph" viewBox="0 0 720 720" aria-hidden="true"><path d="M360 0C161.18 0 0 161.18 0 360c0 65.41 17.45 126.75 47.94 179.61L0 720l187.02-44.21C238.36 703.97 297.3 720 360 720c198.82 0 360-161.18 360-360S558.82 0 360 0Zm0 655.52c-60.17 0-116.13-17.98-162.82-48.87L86.69 634.79l30.99-105.61c-33.53-47.93-53.2-106.26-53.2-169.19 0-163.21 132.31-295.52 295.52-295.52s295.52 132.31 295.52 295.52S523.21 655.52 360 655.52Z"/><path d="m444.35 407.52 87.1 41.06c4 1.88 6.56 5.94 6.2 10.34-.94 11.46-5.54 34.43-26.13 55.02-58.12 58.12-162.49-7.64-166.74-10.18-25.67-13.79-50.06-32.24-73.19-55.36s-41.58-47.52-55.37-73.19c-2.55-4.24-68.31-108.61-10.18-166.74 20.59-20.59 43.56-25.19 55.02-26.13 4.41-.36 8.46 2.2 10.34 6.2l41.07 87.1c1.94 4.12 1.09 9.02-2.13 12.24l-30.61 30.61c-6.62 6.62-8.56 16.93-4 25.11 11.17 20.03 26.19 39.32 43.59 57.07 17.75 17.4 37.04 32.43 57.07 43.59 8.18 4.56 18.48 2.62 25.11-4l30.61-30.61c3.22-3.22 8.12-4.08 12.24-2.13Z"/></svg>'
    default:
      return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2.1Z"></path></svg>'
  }
}

function positionClass(position) {
  switch (position) {
    case "bottom-left":
      return "is-bottom-left"
    case "middle-right":
      return "is-middle-right"
    case "middle-left":
      return "is-middle-left"
    default:
      return ""
  }
}

export function assistantWidgetClickIsOutside(event, host) {
  if (!host) return true

  const composedPath = typeof event?.composedPath === "function" ? event.composedPath() : []
  if (composedPath.includes(host)) return false

  const target = event?.target
  return !(target === host || host.contains?.(target))
}

function escapeHtml(value) {
  const div = document.createElement("div")
  div.textContent = value == null ? "" : String(value)
  return div.innerHTML
}

function escapeAttribute(value) {
  return escapeHtml(value).replace(/"/g, "&quot;")
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true })
  } else {
    init()
  }
}
